import { EcommerceId } from './enums';

interface DialogContentInterface {
    iframeElements: { src: string | null; width: string | null; height: string | null }[];
    instructions: [{ text: string | null | HTMLParagraphElement }?][];
    title: string[];
    label: string;
    bannerContent: string;
    additionalContent: string[];
}

function getEcommerceLabel(value: EcommerceId): string {
    switch (value) {
        case EcommerceId.NONE:
            return 'None';
        case EcommerceId.MAGENTO2:
            return 'Magento 2';
        case EcommerceId.WOOCOMMERCE:
            return 'WooCommerce';
        case EcommerceId.SHOPIFY:
            return 'Shopify';
        case EcommerceId.OPENCART:
            return 'OpenCart';
        case EcommerceId.MAGENTO1:
            return 'Magento 1';
        case EcommerceId.PRESTASHOP:
            return 'PrestaShop';
        case EcommerceId.API:
            return 'API';
        case EcommerceId.BIGCOMMERCE:
            return 'BigCommerce';
        case EcommerceId.CCLOUD:
            return 'cCloud';
        case EcommerceId.ETSY:
            return 'Etsy';
        case EcommerceId.WIX:
            return 'Wix';
        case EcommerceId.SHOPWARE:
            return 'Shopware';
        default:
            return 'Unknown';
    }
}

const getEcommerceID = (zendeskArticleId: string) => {
    switch (zendeskArticleId) {
        case '360024941034':
            return EcommerceId.SHOPIFY;
        case '360024308714':
            return EcommerceId.WOOCOMMERCE;
        case '360025096873':
            return EcommerceId.PRESTASHOP;
        case '360026137574':
            return EcommerceId.BIGCOMMERCE;
        case '360025157553':
            return EcommerceId.MAGENTO2;
        case '360017787320':
            return EcommerceId.OPENCART;
        case '5090942643740':
            return EcommerceId.WIX;
        case '5786054102044':
            return EcommerceId.SHOPWARE;
        default:
            return EcommerceId.NONE;
    }
};

const iframeParser = (doc: Document) => {
    const iframeElements = Array.from(doc.querySelectorAll('iframe')).map((iframe) => ({
        src: iframe.getAttribute('src'),
        width: iframe.getAttribute('width'),
        height: iframe.getAttribute('height')
    }));
    return iframeElements;
};

const instructionsParser = (doc: Document, ecommerceID: EcommerceId) => {
    switch (ecommerceID) {
        case 2:
        case 4:
        case 14:
            return Array.from(doc.querySelectorAll('ol.list-colored'));
        case 3:
            return doc.querySelector('ul.list-colored');
        default:
            return doc.querySelector('ol.list-colored');
    }
};

const instructionItemsParser = (container: Element[] | Element, ecommerceID: EcommerceId) => {
    switch (ecommerceID) {
        case 2: {
            if (Array.isArray(container)) {
                return Array.from(container[1].querySelectorAll('li'))
                    .slice(3)
                    .map((li) => {
                        const textContent = li?.innerHTML.trim();
                        return { text: textContent };
                    });
            }
            break;
        }

        case 4:
        case 14: {
            if (Array.isArray(container)) {
                return Array.from(
                    container.map((item) => {
                        return Array.from(item.querySelectorAll('li')).map((li) => {
                            let textContent = li.innerHTML?.trim();
                            textContent = textContent?.replace(/&gt;&gt;/g, '>')?.replace(/<code>/g, '');
                            return { text: textContent };
                        });
                    })
                );
            }
            break;
        }
        //1, 3, 6, 9
        default:
            if (container instanceof Element) {
                return (
                    Array.from(container.querySelectorAll('li')).map((li) => {
                        if (li.querySelector('ul li p a')) {
                            const nestedLi = li.querySelector('ul');
                            if (nestedLi) {
                                const nestedLiText = nestedLi?.querySelector('li p a');
                                if (nestedLiText) {
                                    return { text: li?.innerHTML?.trim() };
                                }
                            }
                        } else {
                            const textContent = li?.innerHTML?.trim();
                            return { text: textContent };
                        }
                    }) ?? []
                );
            }
            break;
    }
};

const bannerContentParser = (doc: Document, ecommerceID: EcommerceId) => {
    switch (ecommerceID) {
        case 2: {
            const notificationBannerContent = doc.querySelector('ol.list-colored');
            let bannerContent = '';
            Array.from(notificationBannerContent?.querySelectorAll('li') ?? []).map((li) => {
                if (li.querySelector('p')) {
                    const nestedLi = li.querySelector('p');
                    if (nestedLi) {
                        if (bannerContent) {
                            bannerContent = bannerContent + '<br>' + nestedLi?.innerText?.trim();
                        } else {
                            bannerContent = nestedLi?.innerText?.trim();
                        }
                    }
                }
            });
            const content = bannerContent?.replace(/(\r\n|\n|\r)/gm, '');
            const message = `<strong>Important:</strong> ${content}`;
            return message;
        }
        case 3: {
            return '';
            //MP: temporarily commented, because the information reported is not correct, seen from logged in to the Backoffice
            // const notificationBannerContent = doc.querySelector('div.callout.callout--info.callout--dashed');
            // const bannerContent = notificationBannerContent?.querySelector('p');
            // const content = bannerContent?.innerHTML.replace(/(\r\n|\n|\r)/gm, '').replace(/<br\s*\/?>/gi, '');
            //
            // const message = `<strong>Important:</strong> ${content}`;
            // return message;
        }
        case 9: {
            return '';
            //MP: temporarily commented, because the information reported is not correct, seen from logged in to the Backoffice
            // const notificationBannerContent = doc?.querySelector('div.callout.callout--warning');
            // const bannerContent = notificationBannerContent?.querySelector('p');
            // const content = bannerContent?.innerHTML.replace(/(\r\n|\n|\r)/gm, '').replace(/<br\s*\/?>/gi, '');
            //
            // const message = `<strong>Important:</strong> ${content}`;
            // return message;
        }
        case 13: {
            return '';
            //MP: temporarily commented, because the information reported is not correct, seen from logged in to the Backoffice
            // const notificationBannerContent = doc?.querySelectorAll('div.callout.callout--warning');
            // const bannerContent = notificationBannerContent[0]?.querySelector('p');
            // const content = bannerContent?.innerHTML.replace(/(\r\n|\n|\r)/gm, '').replace(/<br\s*\/?>/gi, '');
            //
            // const message = `<strong>Important:</strong> ${content}`;
            // return message;
        }
        default:
            return '';
    }
};

const titleParser = (doc: Document, ecommerceID: EcommerceId) => {
    switch (ecommerceID) {
        case 1: {
            const title = ['To install the Zakeke extension on your Magento 2 / Adobe Commerce store you just have to follow these steps:'];
            return title;
        }
        case 2: {
            const title = ['To integrate Zakeke in your WordPress/WooCommerce website you just have to follow these steps:'];
            return title;
        }
        case 3:
        case 9: {
            const title = [`To install Zakeke in your ${getEcommerceLabel(ecommerceID)} store you just have to follow these steps:`];
            return title;
        }
        case 4: {
            const title = [
                `To integrate Zakeke with <strong>OpenCart 2.3.0.2 & 3</strong> you just have to follow these steps:`,
                `To integrate Zakeke with <strong>OpenCart Cloud</strong> you just have to follow these steps:`
            ];
            return title;
        }
        case 6: {
            const title = ['To integrate Zakeke in your PrestaShop website you just have to follow these steps:'];
            return title;
        }
        case 13:
        case 14: {
            const item =
                doc
                    .querySelector('div.header-title')
                    ?.innerHTML?.trim()
                    ?.replace(/<h2\b[^>]*>/g, '<h4>')
                    ?.replace(/<\/h2>/g, '</h4>') || '';
            return [item];
        }
        default:
            break;
    }
};

export const additionalContentParser = (doc: Document, eCommerceID: EcommerceId) => {
    switch (eCommerceID) {
        case 1: {
            const header = Array.from(doc.querySelectorAll('h2'))
                .filter((h) => !h.closest('ol') && !h.closest('div') && h.textContent?.trim() !== '' && h.textContent?.trim() !== '\u00A0')
                .map((h) => h.innerHTML);

            const paragraphs = Array.from(doc.querySelectorAll('p'))
                .filter((p) => !p.closest('ol') && !p.closest('div') && p.textContent?.trim() !== '' && p.textContent?.trim() !== '\u00A0')
                .map((p) => p.innerHTML?.trim());

            return [
                `<strong>${header[0]}</strong><br> ${paragraphs[1]} <br> ${paragraphs[2]} <br> ${paragraphs[3]}`
                    ?.replace(/<code[^>]*>/g, '')
                    ?.replace(/<\/code>/g, '') ?? ''
            ];
        }
        case 13: {
            const paragraphs = Array.from(doc.querySelectorAll('p'))
                .filter((p) => !p.closest('ol') && !p.closest('div') && p.textContent?.trim() !== '' && p.textContent?.trim() !== '\u00A0')
                .map((p) => p.innerHTML);
            return paragraphs;
        }
        default:
            return [];
    }
};

export const docParser = (doc: Document, zendeskArticleId: string) => {
    const eCommerceID = getEcommerceID(zendeskArticleId);

    let content: DialogContentInterface = {
        iframeElements: [],
        instructions: [] as [{ text: string | null | HTMLParagraphElement }][],
        title: [],
        label: '',
        bannerContent: '',
        additionalContent: []
    };

    const instructionsContainer = instructionsParser(doc, eCommerceID);

    if (instructionsContainer) {
        const instructionItems = instructionItemsParser(instructionsContainer, eCommerceID);

        if (instructionItems && instructionItems.length > 0) {
            content.instructions = [];
            instructionItems.forEach((x, index) => {
                if (Array.isArray(x)) {
                    if (!content.instructions[index]) {
                        content.instructions[index] = [];
                    }
                    x.forEach((y) => {
                        if (y) {
                            content.instructions[index].push({
                                text: y?.text ?? ''
                            });
                        }
                    });
                } else {
                    if (x) {
                        if (!content.instructions[0]) {
                            content.instructions[0] = [];
                        }
                        content.instructions[0].push({
                            text: x?.text ?? ''
                        });
                    }
                }
            });
        }
    }

    content.additionalContent = additionalContentParser(doc, eCommerceID) || [];

    content.title = titleParser(doc, eCommerceID) || [];

    content.bannerContent = bannerContentParser(doc, eCommerceID) || '';

    content.iframeElements = iframeParser(doc) || [];

    return content;
};
